import { lazy, Suspense, useEffect, type ReactNode } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { onAppAccessLost, onUnauthorized, type AppKey } from './lib/api';
import { Locked, NoAccess } from './pages/NoAccess';
import { useMe } from './lib/session';
import { useAnalytics } from './lib/useAnalytics';
import { SignIn, Welcome } from './pages/SignIn';
import { Unsubscribe } from './pages/Unsubscribe';
import { UpdatePrompt } from './components/UpdatePrompt';
import { ImpersonationEnded, ImpersonationBanner } from './components/Impersonation';
import { StagingBanner } from './components/Staging';
import { OldAdmin, SettingsLayout } from './settings/SettingsLayout';
// Each app and each Settings page loads on first visit (22 Oct 2026): opening Task Force no longer downloads the
// reports' charts (Chart.js), the Click Up engine or the admin pages first.
const Report = lazy(() => import('./pages/Report').then((m) => ({ default: m.Report })));
const ClickupReport = lazy(() => import('./pages/ClickupReport').then((m) => ({ default: m.ClickupReport })));
const TaskforceReport = lazy(() => import('./pages/ClickupReport').then((m) => ({ default: m.TaskforceReport })));
const People = lazy(() => import('./settings/People').then((m) => ({ default: m.People })));
const Uploads = lazy(() => import('./admin/Uploads').then((m) => ({ default: m.Uploads })));
const WeeklyEmails = lazy(() => import('./admin/WeeklyEmails').then((m) => ({ default: m.WeeklyEmails })));
const Data = lazy(() => import('./admin/Data').then((m) => ({ default: m.Data })));
const Access = lazy(() => import('./admin/Access').then((m) => ({ default: m.Access })));
const Settings = lazy(() => import('./admin/Settings').then((m) => ({ default: m.Settings })));
const Tier1 = lazy(() => import('./admin/Tier1').then((m) => ({ default: m.Tier1 })));
const Activity = lazy(() => import('./admin/Activity').then((m) => ({ default: m.Activity })));
const Usage = lazy(() => import('./admin/Usage').then((m) => ({ default: m.Usage })));
const ClickUp = lazy(() => import('./admin/ClickUp').then((m) => ({ default: m.ClickUp })));
// Task Force (and its editor) loads on first visit, so the reports do not carry it.
const Taskforce = lazy(() => import('./taskforce/Taskforce').then((m) => ({ default: m.Taskforce })));
const TaskforceSettings = lazy(() => import('./settings/TaskforceSettings'));
// Forms anyone can open (no sign-in) load on their own too.
const PublicFormPage = lazy(() => import('./pages/PublicForm').then((m) => ({ default: m.PublicFormPage })));
const OAuthConsent = lazy(() => import('./pages/OAuthConsent').then((m) => ({ default: m.OAuthConsent })));
const FormUpdates = lazy(() => import('./pages/PublicForm').then((m) => ({ default: m.FormUpdates })));
const page = (el: ReactNode) => <Suspense fallback={<main className="tfna" aria-busy="true" />}>{el}</Suspense>;
/** A Settings page, while its code loads. */
const sub = (el: ReactNode) => <Suspense fallback={<p className="sub">Loading…</p>}>{el}</Suspense>;
const taskforce = (
  <Suspense fallback={<div className="tfboot" aria-busy="true" />}>
    <Taskforce />
  </Suspense>
);
import { startUsage, track } from './lib/usage';

/**
 * Signed-in users only. With `app`, only those who have that app (others see it locked, with Request access); with
 * `hr`, Attendance admins (HR) and super admins. Must-change-password users go back to the sign-in card.
 */
function RequireAuth({ children, hr, app }: { children: ReactNode; hr?: boolean; app?: AppKey }) {
  const me = useMe();
  const location = useLocation();
  const signedIn = !!me.data && !me.data.mustChangePassword;
  useEffect(() => (signedIn ? startUsage() : undefined), [signedIn]);
  useEffect(() => {
    if (signedIn) track('page');
  }, [signedIn, location.pathname]);
  if (me.isLoading) return null;
  if (!me.data || me.data.mustChangePassword)
    return <Navigate to="/sign-in" replace state={{ from: location.pathname + location.search }} />;
  const access = me.data.access;
  // Settings: anyone who manages something; each sees their own sections.
  if (hr && !access?.settings) return <Navigate to="/" replace />;
  // An app they do not have (29 Oct 2026): an old link, a bookmark or a notification takes them to where they land,
  // with no error; choosing the locked app in the app switcher says it is locked and offers Request access.
  if (app && !access?.apps[app])
    return (location.state as { ask?: boolean } | null)?.ask ? (
      <Locked app={app} />
    ) : (
      <Navigate to={access?.home ?? '/no-access'} replace />
    );
  return <>{children}</>;
}

/** "/": where this person lands (Task Force, else their attendance, else Click Up, else "no access yet"). */
function Landing() {
  const me = useMe();
  const location = useLocation();
  if (me.isLoading || !me.data) return null;
  const home = me.data.access?.home ?? '/no-access';
  // Old links to the attendance report (/?member=…) still open it.
  if (location.search && me.data.access?.apps.attendance)
    return <Navigate to={`/attendance${location.search}`} replace />;
  return <Navigate to={home} replace />;
}

export function App() {
  const qc = useQueryClient();
  // Task Force's analytics (30 Oct 2026, docs/ANALYTICS.md): pages, tasks opened, who is signed in.
  useAnalytics();
  // Any 401 (idle timeout, signed out elsewhere) returns to the sign-in screen.
  useEffect(() => onUnauthorized(() => qc.setQueryData(['me'], null)), [qc]);
  // An app taken away while open (the free trial ended): who they are again, so they move to where they land.
  useEffect(() => onAppAccessLost(() => void qc.invalidateQueries({ queryKey: ['me'] })), [qc]);
  return (
    <>
      <StagingBanner />
      <UpdatePrompt />
      <ImpersonationBanner />
      <Routes>
        <Route path="/impersonate" element={<ImpersonationEnded />} />
        <Route path="/sign-in" element={<SignIn />} />
        <Route path="/welcome" element={<Welcome />} />
        <Route path="/email/unsubscribe" element={<Unsubscribe />} />
        <Route path="/f/updates" element={page(<FormUpdates />)} />
        {/* An AI assistant asking to use Task Force as you (29 Oct 2026): signed in first, then Allow or Cancel. */}
        <Route path="/oauth/consent" element={<RequireAuth>{page(<OAuthConsent />)}</RequireAuth>} />
        <Route path="/f/:token" element={page(<PublicFormPage />)} />
        <Route
          path="/"
          element={
            <RequireAuth>
              <Landing />
            </RequireAuth>
          }
        />
        <Route path="/attendance" element={<RequireAuth app="attendance">{page(<Report />)}</RequireAuth>} />
        <Route
          path="/no-access"
          element={
            <RequireAuth>
              <NoAccess />
            </RequireAuth>
          }
        />
        <Route path="/taskforce" element={<RequireAuth app="taskforce">{taskforce}</RequireAuth>} />
        <Route path="/taskforce/:spaceId/:view" element={<RequireAuth app="taskforce">{taskforce}</RequireAuth>} />
        <Route path="/clickup" element={<RequireAuth app="clickup">{page(<ClickupReport />)}</RequireAuth>} />
        {/* The TaskForce Report (29 Oct 2026): the Click Up report's charts from Task Force, for everyone with it. */}
        <Route
          path="/taskforce-report"
          element={<RequireAuth app="taskforce">{page(<TaskforceReport />)}</RequireAuth>}
        />
        <Route
          path="/settings"
          element={
            <RequireAuth hr>
              <SettingsLayout />
            </RequireAuth>
          }
        >
          <Route path="people" element={sub(<People />)} />
          <Route path="uploads" element={sub(<Uploads />)} />
          <Route path="weekly" element={sub(<WeeklyEmails />)} />
          <Route path="data" element={sub(<Data />)} />
          <Route path="accounts" element={sub(<Access />)} />
          <Route path="tier1" element={sub(<Tier1 />)} />
          <Route path="attendance" element={sub(<Settings />)} />
          <Route path="clickup" element={sub(<ClickUp />)} />
          <Route
            path="taskforce"
            element={
              <Suspense fallback={<p className="sub">Loading…</p>}>
                <TaskforceSettings />
              </Suspense>
            }
          />
          <Route path="usage" element={sub(<Usage />)} />
          <Route path="activity" element={sub(<Activity />)} />
        </Route>
        {/* Admin and Task Force's own settings moved into Settings (18 Oct 2026). */}
        <Route path="/admin/*" element={<OldAdmin />} />
        <Route path="/taskforce/-/settings" element={<Navigate to="/settings/taskforce" replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}
